import React, { CSSProperties, ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { TextClamp } from '../../components/text/TextClamp'
import { chartSegmentControlClearanceBottom } from '../../components/chart-segment/ChartSegmentControl'
import { formatQuadrantAxisLabel } from '../../foundations/quadrant-axis-label'
import { visibleTextClampFromSchema } from '../../foundations/text-limits'
import { ChartLabel } from '../_shared/ChartLabel'
import { FastTooltip } from '../_shared/FastTooltip'
import { PlotRenderMode, QuadrantCornerLabels } from '@digital-twins/slide-schemas/generated/plot-types'
import { QUADRANT_PLANE_MIDPOINT, StoredQuadrant } from './adapters'

/**
 * The `quadrant` kind at slide size, card style (plots Wave 7, D1): the deck
 * quadrant-map slide's map. Stage 1 freezes slide geometry, so every number
 * here is the slide's own, moved from `slides/QuadrantMapSlide.tsx` unchanged:
 * the 873x667 frame, its paddings, the corner pills, the midpoint crosshair,
 * the axis titles and the point cards with their measured, collision-avoiding
 * placement. The slide keeps its rail, header, chart title and breakdown
 * control; this draws what sits on the map.
 *
 * Two readers feed it. A slide built with `plot` draws Python's stamps
 * (`slideMapFromPlot`): the points, badges and images on the plot, its axis
 * titles, midpoint and corner names. A slide stored before the stamp keeps the
 * slide's own legacy reader (`readLegacyQuadrantMapPoints` in the slide).
 */

/**
 * Top-row quadrant labels start below the breakdown control's reserved region, so the
 * control — and the pill it unfurls on hover, which used to print straight over
 * "Scale Ready" — never lands on them. Derived from the shared clearance rather than a
 * hand-tuned number, so the next change to the control moves these labels with it.
 */
const QUADRANT_TOP_LABEL_GAP = 6
const QUADRANT_TOP_LABEL_HEIGHT = 40
const QUADRANT_TOP_LABEL_TOP_PX = chartSegmentControlClearanceBottom() + QUADRANT_TOP_LABEL_GAP
const QUADRANT_TOP_LABEL_TOP = `${QUADRANT_TOP_LABEL_TOP_PX}px`

/**
 * The slide map's frame, in the units every position below is computed in: the
 * quadrant-map slide's rail-wide plot slot, 872x674 (plots plan AG; 873x667 before).
 * A plot constant, so the plot library does not read slide layouts; a deck-ui test
 * holds it equal to the slide's binding.
 */
export const QUADRANT_SLIDE_FRAME = {
  width: 872,
  height: 674,
  // Plotting starts where the top quadrant labels end.
  paddingTop: QUADRANT_TOP_LABEL_TOP_PX + QUADRANT_TOP_LABEL_HEIGHT,
  paddingRight: 27,
  paddingBottom: 77,
  paddingLeft: 56,
}

const CHART_FRAME = QUADRANT_SLIDE_FRAME

/**
 * The card's fixed width and its minimum height. A card with no image grows downward
 * to hold its whole name, so placement reads each card's measured box; these numbers
 * stand in only where nothing has been measured (jsdom, first layout pass).
 */
const POINT_CARD = {
  width: 109,
  height: 61,
  badgeSize: 26,
  /** A text card prints at most this many lines of the name (decision 21). */
  labelMaxLines: 2,
}

const BADGE_STYLE = {
  background: 'var(--ds-quadrant-badge-bg)',
  border: '1px solid var(--ds-quadrant-badge-border)',
  color: 'var(--ds-quadrant-legend-badge-fg)',
} as const

const SLIDE_TYPE_FOR_LIMITS = 'quadrant-map'

/** One drawable point card on the slide map. */
export interface QuadrantSlideMapPoint {
  /** The key a card is measured under: the item id, else its label. */
  key: string
  label: string
  /** Position in the reader's source list, the index an edit of its name writes to. */
  sourceIndex: number
  x: number
  y: number
  badgeLabel: string
  imageUrl: string | null
}

/** What the slide map draws, whichever reader produced it. */
export interface QuadrantSlideMapModel {
  points: Array<QuadrantSlideMapPoint>
  xAxisLabel: string
  yAxisLabel: string
  xAxisFullLabel: string | null
  yAxisFullLabel: string | null
  midpoint: { x: number; y: number }
  /** Each corner prints when it has a name (the plot stamps all four or none). */
  cornerLabels: Partial<QuadrantCornerLabels> | null
}

export interface QuadrantSlideMapLabelArgs {
  point: QuadrantSlideMapPoint
  style: CSSProperties
}

export interface QuadrantSlideMapProps {
  model: QuadrantSlideMapModel
  renderMode?: PlotRenderMode
  /** The deck passes its editable text; anyone else gets the name as plain text. */
  renderPointLabel?: (args: QuadrantSlideMapLabelArgs) => ReactNode
  /**
   * Keep the x-axis title clear of the bottom corner names (the stamped plot). Off
   * for the legacy reader: a stored slide draws exactly as it always did.
   */
  keepXTitleClearOfCorners?: boolean
  /** The map layer's test id. */
  testId?: string
}

/** The badge a point without one prints: its letter in the drawn order. */
function fallbackBadge(index: number): string {
  return String.fromCharCode(65 + index)
}

/**
 * A stamped plot as the slide map's model. Shape only: Python's points in its
 * order, a point with a null axis or no name not drawn (never at 0), the
 * stamped badge, image, axis titles, midpoint and corner names.
 */
export function slideMapFromPlot(data: StoredQuadrant['data']): QuadrantSlideMapModel {
  const points: Array<QuadrantSlideMapPoint> = []
  data.points.forEach((point, index) => {
    const x = typeof point.x === 'number' && Number.isFinite(point.x) ? point.x : null
    const y = typeof point.y === 'number' && Number.isFinite(point.y) ? point.y : null
    const label = (point.label || '').replace(/\s+/g, ' ').trim()
    if (x === null || y === null || !label) {
      return
    }
    points.push({
      key: point.id || label,
      label,
      sourceIndex: index,
      x,
      y,
      badgeLabel: (point.badge || fallbackBadge(index)).trim(),
      imageUrl: point.imageUrl || null,
    })
  })
  return {
    points,
    xAxisLabel: formatQuadrantAxisLabel(data.axes.x.label || ''),
    yAxisLabel: formatQuadrantAxisLabel(data.axes.y.label || ''),
    xAxisFullLabel: data.axes.x.fullLabel ?? null,
    yAxisFullLabel: data.axes.y.fullLabel ?? null,
    midpoint: data.midpoint ?? { ...QUADRANT_PLANE_MIDPOINT },
    cornerLabels: data.cornerLabels ?? null,
  }
}

/** A point card's box, in chart-frame units (the units `leftPx`/`topPx` are in). */
interface CardSize {
  width: number
  height: number
}

interface PositionedQuadrantPoint extends QuadrantSlideMapPoint, CardSize {
  leftPx: number
  topPx: number
}

function clampValue(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function rectanglesOverlap(a: PositionedQuadrantPoint, b: PositionedQuadrantPoint, gap: number): boolean {
  return (
    Math.abs(a.leftPx - b.leftPx) < (a.width + b.width) / 2 + gap &&
    Math.abs(a.topPx - b.topPx) < (a.height + b.height) / 2 + gap
  )
}

function resolvePointPlacements(
  points: Array<QuadrantSlideMapPoint>,
  plotWidth: number,
  plotHeight: number,
  cardSizes: ReadonlyMap<string, CardSize>,
): Array<PositionedQuadrantPoint> {
  const offsetCandidates = [{ x: 0, y: 0 }]
  const radii = [40, 56, 72, 88, 104, 124, 144, 164]
  const directions = [
    { x: 1, y: 0 },
    { x: -1, y: 0 },
    { x: 0, y: 1 },
    { x: 0, y: -1 },
    { x: 0.7, y: 0.7 },
    { x: -0.7, y: 0.7 },
    { x: 0.7, y: -0.7 },
    { x: -0.7, y: -0.7 },
    { x: 0.9, y: 0.4 },
    { x: -0.9, y: 0.4 },
    { x: 0.9, y: -0.4 },
    { x: -0.9, y: -0.4 },
  ]
  for (const radius of radii) {
    for (const direction of directions) {
      offsetCandidates.push({
        x: Math.round(direction.x * radius),
        y: Math.round(direction.y * radius),
      })
    }
  }

  const AXIS_LABEL_INSET_LEFT = 20
  const AXIS_LABEL_INSET_BOTTOM = 20
  const placed: Array<PositionedQuadrantPoint> = []

  for (const point of points) {
    const size = cardSizes.get(point.key) ?? {
      width: POINT_CARD.width,
      height: POINT_CARD.height,
    }
    const minLeft = CHART_FRAME.paddingLeft + size.width / 2 + AXIS_LABEL_INSET_LEFT
    const maxLeft = CHART_FRAME.paddingLeft + plotWidth - size.width / 2
    const minTop = CHART_FRAME.paddingTop + size.height / 2
    const maxTop = CHART_FRAME.paddingTop + plotHeight - size.height / 2 - AXIS_LABEL_INSET_BOTTOM
    const targetLeft = CHART_FRAME.paddingLeft + (plotWidth * point.x) / 100
    const targetTop = CHART_FRAME.paddingTop + (plotHeight * (100 - point.y)) / 100

    let chosenLeft = clampValue(targetLeft, minLeft, maxLeft)
    let chosenTop = clampValue(targetTop, minTop, maxTop)
    let bestPenalty = Number.POSITIVE_INFINITY

    for (const offset of offsetCandidates) {
      const candidateLeft = clampValue(targetLeft + offset.x, minLeft, maxLeft)
      const candidateTop = clampValue(targetTop + offset.y, minTop, maxTop)
      const candidate = {
        ...point,
        ...size,
        leftPx: candidateLeft,
        topPx: candidateTop,
      }
      const overlapCount = placed.reduce(
        (count, placedPoint) => count + (rectanglesOverlap(candidate, placedPoint, 8) ? 1 : 0),
        0,
      )
      const penalty =
        Math.abs(candidateLeft - targetLeft) + Math.abs(candidateTop - targetTop) + overlapCount * 10000
      if (penalty < bestPenalty) {
        bestPenalty = penalty
        chosenLeft = candidateLeft
        chosenTop = candidateTop
      }
      if (overlapCount === 0) {
        chosenLeft = candidateLeft
        chosenTop = candidateTop
        break
      }
    }

    placed.push({
      ...point,
      ...size,
      leftPx: chosenLeft,
      topPx: chosenTop,
    })
  }

  return placed
}

/**
 * Each point card's rendered box, converted into chart-frame units. A card's width is
 * fixed and its height follows only its own wrapped name, so the measurement does not
 * depend on where placement puts it. Positions are percentages of the chart while the
 * card is sized in CSS px, hence the conversion through the chart's rendered width.
 *
 * jsdom reports 0 for every box, so under test nothing is measured and placement uses
 * the nominal `POINT_CARD` box. jsdom has no ResizeObserver either, which degrades
 * this to the one measurement.
 */
function useMeasuredCardSizes(cardKey: string) {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const cardRefs = useRef(new Map<string, HTMLElement>())
  const [cardSizes, setCardSizes] = useState<ReadonlyMap<string, CardSize>>(() => new Map())

  useLayoutEffect(() => {
    const chart = chartRef.current
    if (!chart) {
      return
    }

    const measure = () => {
      const chartWidth = chart.getBoundingClientRect().width
      if (chartWidth <= 0) {
        return
      }
      const toFrame = CHART_FRAME.width / chartWidth
      const next = new Map<string, CardSize>()
      cardRefs.current.forEach((card, label) => {
        const rect = card.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          next.set(label, {
            width: rect.width * toFrame,
            height: rect.height * toFrame,
          })
        }
      })
      setCardSizes((previous) => {
        const unchanged =
          previous.size === next.size &&
          Array.from(next).every(([label, size]) => {
            const before = previous.get(label)
            return (
              before !== undefined &&
              Math.abs(before.width - size.width) < 0.5 &&
              Math.abs(before.height - size.height) < 0.5
            )
          })
        return unchanged ? previous : next
      })
    }

    measure()

    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(chart)
    cardRefs.current.forEach((card) => observer.observe(card))
    return () => {
      observer.disconnect()
    }
  }, [cardKey])

  const registerCard = (label: string) => (node: HTMLElement | null) => {
    if (node) {
      cardRefs.current.set(label, node)
    } else {
      cardRefs.current.delete(label)
    }
  }

  return { chartRef, registerCard, cardSizes }
}

/** The clear space kept between the x-axis title and a bottom corner name. */
const X_TITLE_CORNER_GAP_PX = 8
/** The bottom corner pills' offset from the map's bottom edge. */
const X_TITLE_PILL_BOTTOM_PX = 12
/** The space between a lifted x-axis title and the pills below it. */
const X_TITLE_LIFT_GAP_PX = 4

/** How the x-axis title and the bottom corner names share the bottom band. */
interface XTitleLayout {
  /** The title's max width (px), centred where it always was; null: as drawn before. */
  maxWidth: number | null
  /** A bottom corner pill's max width (px), so its name wraps to two lines; null: natural. */
  leftCap: number | null
  rightCap: number | null
  /** The title's bottom (px) when it is lifted above the pills at full plot width. */
  liftBottom: number | null
}

const NO_X_TITLE_LAYOUT: XTitleLayout = { maxWidth: null, leftCap: null, rightCap: null, liftBottom: null }

function sameXTitleLayout(a: XTitleLayout, b: XTitleLayout): boolean {
  const near = (x: number | null, y: number | null) => (x === null || y === null ? x === y : Math.abs(x - y) < 0.5)
  return near(a.maxWidth, b.maxWidth) && near(a.leftCap, b.leftCap) && near(a.rightCap, b.rightCap) && near(a.liftBottom, b.liftBottom)
}

/** Whether a clamped text box hides any of its text. */
function isTextCut(text: HTMLElement): boolean {
  return text.scrollHeight > text.clientHeight + 1 || text.scrollWidth > text.clientWidth + 1
}

/**
 * The bottom band of a stamped map (Wave 7 D1, review M1), measured in the map's own
 * CSS px; jsdom lays out nothing and gets `NO_X_TITLE_LAYOUT`. In order:
 *
 * 1. The x-axis title fits its two lines in the room between the bottom corner names
 *    (twice the room from its centre to the nearer pill): it keeps its centre and only
 *    narrows, so a title that fits draws exactly as before.
 * 2. It does not: each bottom pill is capped at the narrowest width that still holds
 *    its name in two lines, and the title gets the room that frees.
 * 3. It still does not: the pills keep their names on one line and the title is lifted
 *    above them, at the full plot width.
 *
 * The measurement sets trial widths on the live nodes and restores them before the
 * effect returns, so nothing is painted in a trial state.
 */
function useXTitleLayout(
  layerRef: React.RefObject<HTMLDivElement | null>,
  layoutKey: string,
  isEnabled: boolean,
): XTitleLayout {
  const [layout, setLayout] = useState<XTitleLayout>(NO_X_TITLE_LAYOUT)

  useLayoutEffect(() => {
    const layer = layerRef.current
    const commit = (next: XTitleLayout) => {
      setLayout((previous) => (sameXTitleLayout(previous, next) ? previous : next))
    }
    if (!layer || !isEnabled) {
      commit(NO_X_TITLE_LAYOUT)
      return
    }
    const find = (testId: string) => layer.querySelector<HTMLElement>(`[data-testid="${testId}"]`)
    const measure = () => {
      const width = layer.offsetWidth
      const left = find('quadrant-map-corner-bottom-left')
      const right = find('quadrant-map-corner-bottom-right')
      const text = find('quadrant-x-axis-label')
      const row = find('quadrant-x-axis-title-row') ?? text
      if (width <= 0 || (!left && !right) || !text || !row) {
        commit(NO_X_TITLE_LAYOUT)
        return
      }
      const pills = [left, right].filter((pill): pill is HTMLElement => pill !== null)
      const saved = [...pills, row].map((node) => ({ node, maxWidth: node.style.maxWidth }))
      const centre =
        (width * (CHART_FRAME.paddingLeft + (CHART_FRAME.width - CHART_FRAME.paddingLeft - CHART_FRAME.paddingRight) / 2)) /
        CHART_FRAME.width
      const roomNow = (): number | null => {
        const halves: Array<number> = []
        if (left && left.offsetWidth > 0) {
          halves.push(centre - (left.offsetLeft + left.offsetWidth))
        }
        if (right && right.offsetWidth > 0) {
          halves.push(right.offsetLeft - centre)
        }
        return halves.length === 0 ? null : Math.max(0, 2 * (Math.min(...halves) - X_TITLE_CORNER_GAP_PX))
      }
      const titleFits = (room: number): boolean => {
        row.style.maxWidth = `${room}px`
        return !isTextCut(text)
      }
      // The narrowest pill width that still shows the whole name (two lines at most).
      const capOf = (pill: HTMLElement | null): number | null => {
        const name = pill?.firstElementChild as HTMLElement | null
        if (!pill || !name) {
          return null
        }
        const natural = pill.offsetWidth
        const minWidth = parseFloat(getComputedStyle(pill).minWidth) || 0
        const fitsAt = (candidate: number) => {
          pill.style.maxWidth = `${candidate}px`
          return !isTextCut(name)
        }
        let low = Math.ceil(minWidth)
        let high = Math.ceil(natural)
        if (low >= high || fitsAt(low)) {
          return low < high ? low : null
        }
        while (high - low > 1) {
          const middle = Math.floor((low + high) / 2)
          if (fitsAt(middle)) {
            high = middle
          } else {
            low = middle
          }
        }
        return high < natural ? high : null
      }

      let next: XTitleLayout = NO_X_TITLE_LAYOUT
      pills.forEach((pill) => {
        pill.style.maxWidth = ''
      })
      const naturalRoom = roomNow()
      if (naturalRoom !== null) {
        if (titleFits(naturalRoom)) {
          next = { ...NO_X_TITLE_LAYOUT, maxWidth: naturalRoom }
        } else {
          const leftCap = capOf(left)
          const rightCap = capOf(right)
          if (left) {
            left.style.maxWidth = leftCap === null ? '' : `${leftCap}px`
          }
          if (right) {
            right.style.maxWidth = rightCap === null ? '' : `${rightCap}px`
          }
          const cappedRoom = roomNow()
          if (cappedRoom !== null && titleFits(cappedRoom)) {
            next = { maxWidth: cappedRoom, leftCap, rightCap, liftBottom: null }
          } else {
            pills.forEach((pill) => {
              pill.style.maxWidth = ''
            })
            const band = Math.max(...pills.map((pill) => pill.offsetHeight))
            next = { ...NO_X_TITLE_LAYOUT, liftBottom: X_TITLE_PILL_BOTTOM_PX + band + X_TITLE_LIFT_GAP_PX }
          }
        }
      }
      saved.forEach(({ node, maxWidth }) => {
        node.style.maxWidth = maxWidth
      })
      commit(next)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(layer)
    // A pill and the title change size with their words (and when the web font arrives).
    ;['quadrant-map-corner-bottom-left', 'quadrant-map-corner-bottom-right', 'quadrant-x-axis-label'].forEach((testId) => {
      const node = find(testId)
      if (node) {
        observer.observe(node)
      }
    })
    return () => {
      observer.disconnect()
    }
  }, [isEnabled, layerRef, layoutKey])

  return layout
}

const CORNER_TEXT_STYLE: CSSProperties = {
  fontSize: '16px',
  fontWeight: 'var(--ds-weight-medium)',
  color: 'var(--ds-exec-scr-body)',
}

export function QuadrantSlideMap(props: QuadrantSlideMapProps) {
  const {
    model,
    renderMode = 'interactive',
    renderPointLabel,
    keepXTitleClearOfCorners = false,
    testId = 'quadrant-map-plot',
  } = props

  const quadrantLabels = model.cornerLabels
  const xMid = model.midpoint.x
  const yMid = model.midpoint.y
  const resolvedXAxisLabel = model.xAxisLabel
  const resolvedYAxisLabel = model.yAxisLabel
  /**
   * D17: the deck is rendered server side through a headless browser into a PDF, where
   * no pointer ever arrives: the axis titles print as plain clamped text and a card's
   * name carries no hover title.
   */
  const isStaticRender = renderMode === 'static'
  const quadrantLabelClamp = visibleTextClampFromSchema('quadrantLabel', SLIDE_TYPE_FOR_LIMITS, {
    lineHeight: '1.4em',
  })
  const itemNameClamp = visibleTextClampFromSchema('itemName', SLIDE_TYPE_FOR_LIMITS, {
    lineHeight: '1.4em',
  })
  const xAxisLabelClamp = visibleTextClampFromSchema('xAxisLabel', SLIDE_TYPE_FOR_LIMITS, {
    lineHeight: '1.4em',
  })
  const yAxisLabelClamp = visibleTextClampFromSchema('yAxisLabel', SLIDE_TYPE_FOR_LIMITS, {
    lineHeight: '1.4em',
  })

  const plotWidth = CHART_FRAME.width - CHART_FRAME.paddingLeft - CHART_FRAME.paddingRight
  const plotHeight = CHART_FRAME.height - CHART_FRAME.paddingTop - CHART_FRAME.paddingBottom
  const { chartRef, registerCard, cardSizes } = useMeasuredCardSizes(
    model.points.map((point) => `${point.key}\u0000${point.imageUrl ?? ''}`).join('\u0001'),
  )
  const placedPoints = useMemo(
    () => resolvePointPlacements(model.points, plotWidth, plotHeight, cardSizes),
    [cardSizes, plotHeight, plotWidth, model.points],
  )
  const xTitleLayout = useXTitleLayout(
    chartRef,
    [quadrantLabels?.bottomLeft ?? '', quadrantLabels?.bottomRight ?? '', resolvedXAxisLabel, renderMode].join('\u0001'),
    keepXTitleClearOfCorners,
  )
  const xTitleMaxWidth = xTitleLayout.maxWidth === null ? undefined : `${xTitleLayout.maxWidth}px`
  const xTitleBottom =
    xTitleLayout.liftBottom === null
      ? `${((CHART_FRAME.paddingBottom * 0.15) / CHART_FRAME.height) * 100}%`
      : `${xTitleLayout.liftBottom}px`

  const pointLabelStyle: CSSProperties = {
    minWidth: 0,
    lineHeight: itemNameClamp.lineHeight,
    overflowWrap: 'anywhere',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: POINT_CARD.labelMaxLines,
    overflow: 'hidden',
  }

  // The map's own layer: the frame every percentage below is a share of, and the box
  // the cards are measured against. It fills the chart exactly and opens no stacking
  // context, so the chart title and the breakdown control keep their order above it.
  return (
    <div ref={chartRef} data-testid={testId} style={{ position: 'absolute', inset: 0 }}>
      {quadrantLabels?.topLeft ? (
        <div
          data-testid="quadrant-map-corner-top-left"
          style={{
            position: 'absolute',
            // Clears the breakdown control, which sits in this box's top-right
            // corner and covered the "Scale Ready" label when its label
            // unfurled on hover. Both top labels move so the pair stays level.
            top: QUADRANT_TOP_LABEL_TOP,
            left: '88px',
            zIndex: 3,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            minHeight: `${QUADRANT_TOP_LABEL_HEIGHT}px`,
            padding: '3px 13px',
            borderRadius: '999px',
            background: 'var(--ds-quadrant-map-bg)',
          }}
        >
          <TextClamp
            minLines={quadrantLabelClamp.minLines}
            maxLines={quadrantLabelClamp.maxLines}
            lineHeight={quadrantLabelClamp.lineHeight}
            style={CORNER_TEXT_STYLE}
          >
            {quadrantLabels.topLeft}
          </TextClamp>
        </div>
      ) : null}
      {quadrantLabels?.topRight ? (
        <div
          data-testid="quadrant-map-corner-top-right"
          style={{
            position: 'absolute',
            top: QUADRANT_TOP_LABEL_TOP,
            right: '40px',
            zIndex: 3,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '132px',
            minHeight: `${QUADRANT_TOP_LABEL_HEIGHT}px`,
            padding: '3px 13px',
            borderRadius: '999px',
            background: 'var(--ds-quadrant-map-bg)',
          }}
        >
          <TextClamp
            minLines={quadrantLabelClamp.minLines}
            maxLines={quadrantLabelClamp.maxLines}
            lineHeight={quadrantLabelClamp.lineHeight}
            style={CORNER_TEXT_STYLE}
          >
            {quadrantLabels.topRight}
          </TextClamp>
        </div>
      ) : null}
      {quadrantLabels?.bottomLeft ? (
        <div
          data-testid="quadrant-map-corner-bottom-left"
          style={{
            position: 'absolute',
            left: '84px',
            bottom: `${X_TITLE_PILL_BOTTOM_PX}px`,
            maxWidth: xTitleLayout.leftCap === null ? undefined : `${xTitleLayout.leftCap}px`,
            zIndex: 3,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '136px',
            minHeight: `${QUADRANT_TOP_LABEL_HEIGHT}px`,
            padding: '3px 13px',
            borderRadius: '999px',
            background: 'var(--ds-quadrant-map-bg)',
          }}
        >
          <TextClamp
            minLines={quadrantLabelClamp.minLines}
            maxLines={quadrantLabelClamp.maxLines}
            lineHeight={quadrantLabelClamp.lineHeight}
            style={CORNER_TEXT_STYLE}
          >
            {quadrantLabels.bottomLeft}
          </TextClamp>
        </div>
      ) : null}
      {quadrantLabels?.bottomRight ? (
        <div
          data-testid="quadrant-map-corner-bottom-right"
          style={{
            position: 'absolute',
            right: '28px',
            bottom: `${X_TITLE_PILL_BOTTOM_PX}px`,
            maxWidth: xTitleLayout.rightCap === null ? undefined : `${xTitleLayout.rightCap}px`,
            zIndex: 3,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '148px',
            minHeight: `${QUADRANT_TOP_LABEL_HEIGHT}px`,
            padding: '3px 13px',
            borderRadius: '999px',
            background: 'var(--ds-quadrant-map-bg)',
          }}
        >
          <TextClamp
            minLines={quadrantLabelClamp.minLines}
            maxLines={quadrantLabelClamp.maxLines}
            lineHeight={quadrantLabelClamp.lineHeight}
            style={CORNER_TEXT_STYLE}
          >
            {quadrantLabels.bottomRight}
          </TextClamp>
        </div>
      ) : null}

      {/* Grid lines — midpoint crosshair only, where the payload stamped it
          (plots plan O; points sit on the 0-100 plane, so 50 is the middle). */}
      <div
        data-testid="quadrant-map-crosshair-x"
        style={{
          position: 'absolute',
          left: `${((CHART_FRAME.paddingLeft + (plotWidth * xMid) / 100) / CHART_FRAME.width) * 100}%`,
          top: `${(CHART_FRAME.paddingTop / CHART_FRAME.height) * 100}%`,
          height: `${(plotHeight / CHART_FRAME.height) * 100}%`,
          borderLeft: '1px solid var(--ds-quadrant-grid-line, var(--ds-border-color))',
        }}
      />
      <div
        data-testid="quadrant-map-crosshair-y"
        style={{
          position: 'absolute',
          left: `${(CHART_FRAME.paddingLeft / CHART_FRAME.width) * 100}%`,
          top: `${((CHART_FRAME.paddingTop + plotHeight * (1 - yMid / 100)) / CHART_FRAME.height) * 100}%`,
          width: `${(plotWidth / CHART_FRAME.width) * 100}%`,
          borderTop: '1px solid var(--ds-quadrant-grid-line, var(--ds-border-color))',
        }}
      />

      {/* Y-axis label — left side, rotated vertically */}
      {resolvedYAxisLabel && !isStaticRender ? (
        <div
          data-testid="quadrant-y-axis-title-row"
          style={{
            position: 'absolute',
            left: `${(CHART_FRAME.paddingLeft / 2 / CHART_FRAME.width) * 100}%`,
            top: `${((CHART_FRAME.paddingTop + plotHeight / 2) / CHART_FRAME.height) * 100}%`,
            transform: 'translate(-50%, -50%) rotate(-90deg)',
            transformOrigin: 'center center',
            color: 'var(--ds-text-muted)',
            fontSize: '11px',
            fontWeight: 'var(--ds-weight-medium)',
            width: plotHeight,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 4,
            zIndex: 2,
          }}
        >
          <ChartLabel
            data-testid="quadrant-y-axis-label"
            text={resolvedYAxisLabel}
            fullText={model.yAxisFullLabel}
            hintIndex={1}
            hintSize={11}
            maxLines={yAxisLabelClamp.maxLines}
            lineHeight={yAxisLabelClamp.lineHeight}
            style={{ textAlign: 'center', overflowWrap: 'anywhere', minWidth: 0 }}
          />
        </div>
      ) : resolvedYAxisLabel ? (
        <div
          data-testid="quadrant-y-axis-label"
          title={resolvedYAxisLabel}
          aria-label={resolvedYAxisLabel}
          style={{
            position: 'absolute',
            left: `${(CHART_FRAME.paddingLeft / 2 / CHART_FRAME.width) * 100}%`,
            top: `${((CHART_FRAME.paddingTop + plotHeight / 2) / CHART_FRAME.height) * 100}%`,
            transform: 'translate(-50%, -50%) rotate(-90deg)',
            transformOrigin: 'center center',
            color: 'var(--ds-text-muted)',
            fontSize: '11px',
            fontWeight: 'var(--ds-weight-medium)',
            lineHeight: yAxisLabelClamp.lineHeight,
            width: plotHeight,
            textAlign: 'center',
            overflowWrap: 'anywhere',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            // D5: wired to the schema clamp (maxLines: 2), never a hardcoded literal.
            WebkitLineClamp: yAxisLabelClamp.maxLines,
            overflow: 'hidden',
            zIndex: 2,
          }}
        >
          {resolvedYAxisLabel}
        </div>
      ) : null}
      {/* X-axis label — bottom center */}
      {resolvedXAxisLabel && !isStaticRender ? (
        <div
          data-testid="quadrant-x-axis-title-row"
          style={{
            position: 'absolute',
            left: `${((CHART_FRAME.paddingLeft + plotWidth / 2) / CHART_FRAME.width) * 100}%`,
            bottom: xTitleBottom,
            transform: 'translateX(-50%)',
            color: 'var(--ds-text-muted)',
            fontSize: '11px',
            fontWeight: 'var(--ds-weight-medium)',
            width: `${(plotWidth / CHART_FRAME.width) * 100}%`,
            maxWidth: xTitleMaxWidth,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 4,
            zIndex: 2,
          }}
        >
          <ChartLabel
            data-testid="quadrant-x-axis-label"
            text={resolvedXAxisLabel}
            fullText={model.xAxisFullLabel}
            hintIndex={0}
            hintSize={11}
            maxLines={xAxisLabelClamp.maxLines}
            lineHeight={xAxisLabelClamp.lineHeight}
            style={{ textAlign: 'center', overflowWrap: 'anywhere', minWidth: 0 }}
          />
        </div>
      ) : resolvedXAxisLabel ? (
        <div
          data-testid="quadrant-x-axis-label"
          title={resolvedXAxisLabel}
          aria-label={resolvedXAxisLabel}
          style={{
            position: 'absolute',
            left: `${((CHART_FRAME.paddingLeft + plotWidth / 2) / CHART_FRAME.width) * 100}%`,
            bottom: xTitleBottom,
            transform: 'translateX(-50%)',
            color: 'var(--ds-text-muted)',
            fontSize: '11px',
            fontWeight: 'var(--ds-weight-medium)',
            lineHeight: xAxisLabelClamp.lineHeight,
            width: `${(plotWidth / CHART_FRAME.width) * 100}%`,
            maxWidth: xTitleMaxWidth,
            textAlign: 'center',
            overflowWrap: 'anywhere',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            // D5: see the Y-axis label above.
            WebkitLineClamp: xAxisLabelClamp.maxLines,
            overflow: 'hidden',
            zIndex: 2,
          }}
        >
          {resolvedXAxisLabel}
        </div>
      ) : null}

      {placedPoints.map((point) => (
        <div
          key={`point-${point.sourceIndex}`}
          ref={registerCard(point.key)}
          data-testid={`quadrant-map-point-card-${point.badgeLabel}`}
          data-left-px={point.leftPx}
          data-top-px={point.topPx}
          style={{
            position: 'absolute',
            left: `${(point.leftPx / CHART_FRAME.width) * 100}%`,
            top: `${(point.topPx / CHART_FRAME.height) * 100}%`,
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            width: `${POINT_CARD.width}px`,
            // A name card grows to hold the whole name; an image card keeps
            // the fixed box its image is cropped to.
            height: point.imageUrl ? `${POINT_CARD.height}px` : undefined,
            minHeight: `${POINT_CARD.height}px`,
            borderRadius: '7px',
            overflow: 'hidden',
            background: 'var(--ds-quadrant-media-card-bg)',
            border: '2px solid var(--ds-quadrant-badge-border)',
            zIndex: 4,
          }}
        >
          {point.imageUrl ? (
            <img
              src={point.imageUrl}
              alt={point.label}
              style={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover',
                objectPosition: 'top center',
              }}
            />
          ) : (
            <FastTooltip label={isStaticRender ? undefined : point.label}>
              <div
                data-testid={`quadrant-map-point-label-${point.badgeLabel}`}
                // The full name on hover, where a pointer can reach it; a
                // static export has none (decision 21).
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  // The bottom inset clears the corner badge, so the last
                  // wrapped line is never painted under it.
                  padding: `8px 8px ${POINT_CARD.badgeSize - 7}px`,
                  color: 'var(--ds-quadrant-legend-badge-fg)',
                  fontSize: '12px',
                  fontWeight: 'var(--ds-weight-medium)',
                  textAlign: 'center',
                }}
              >
              {/* The name, wrapped and clamped to two lines with an ellipsis
                  (plots plan O, decision 21): the full name lives in the rail row
                  and its narrative. Placement still spaces the cards by their
                  measured boxes, and a clamped card keeps the 873x667 map
                  readable at any name length. */}
              {renderPointLabel ? (
                renderPointLabel({ point, style: pointLabelStyle })
              ) : (
                <span style={pointLabelStyle}>{point.label}</span>
              )}
              </div>
            </FastTooltip>
          )}
          <div
            style={{
              position: 'absolute',
              right: '-7px',
              bottom: '-7px',
              width: `${POINT_CARD.badgeSize}px`,
              height: `${POINT_CARD.badgeSize}px`,
              borderRadius: '4px',
              background: BADGE_STYLE.background,
              border: BADGE_STYLE.border,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: BADGE_STYLE.color,
              fontSize: '12px',
              fontWeight: 'var(--ds-weight-medium)',
            }}
          >
            {point.badgeLabel}
          </div>
        </div>
      ))}
    </div>
  )
}
